import { useLanguage } from '@/app/providers';
import {
   AudioIcon,
   CameraIcon,
   ContactIcon,
   DocumentIcon,
   IconBlock,
   IconCalendar,
   IconMic,
   LocationIcon,
   VideocallIcon,
} from '@/assets/images';
import { formatMeetingDate } from '@/features/timeformatter';
import { getLocalUserId } from '@/shared/utils';
import { useMemo } from 'react';
import { useGroupNotificationText } from './useGroupNotificationText';
import { useMessageFormatter } from './useMessageFormatter';

const OWNABLE_MSG_TYPES = ['text', 'image', 'video', 'audio', 'location', 'file', 'contact', 'meet'];

/**
 * Derives the last-message preview (icon + label) for any chat-shaped item.
 * Shared between recent-chat list and message-request list so the preview
 * stays consistent across both surfaces.
 */
export const useLastMessageDisplay = item => {
   const { t } = useLanguage();
   const localuser = getLocalUserId();
   const isMyMessage = localuser === item?.publisherId && OWNABLE_MSG_TYPES.includes(item?.msgType);

   const formattedText = useMessageFormatter(item?.msgBody?.message, {
      mentionedUsersIds: item?.msgBody?.mentionedUsersIds || [],
      plainText: true,
   });

   const groupNotificationText = useGroupNotificationText(item);

   const lastMessageDisplay = useMemo(() => {
      switch (true) {
         case item?.deleteStatus === 1:
            return {
               icon: <IconBlock className="chat-message-icon" />,
               label: isMyMessage ? t('message.YOU_DELETED_THIS_MESSAGE') : t('message.OTHER_DELETED_THIS_MESSAGE'),
            };
         case item?.msgType === 'text' || item?.msgType === 'auto_text':
            return { icon: null, label: formattedText };
         case item?.msgType === 'groupProfileUpdated':
            return { icon: null, label: groupNotificationText };
         case item?.msgType === 'image':
            return { icon: <CameraIcon className="chat-message-icon" />, label: 'Image' };
         case item?.msgType === 'video':
            return { icon: <VideocallIcon className="chat-message-icon" />, label: 'Video' };
         case item?.msgType === 'audio' || item?.msgType === 'audio_recorded': {
            const isRecording = item?.msgBody?.media?.audioType === 'recording';
            return {
               icon: isRecording ? (
                  <IconMic className="chat-message-icon" />
               ) : (
                  <AudioIcon className="chat-message-icon" />
               ),
               label: isRecording ? t('chat.VOICE_MESSAGE') : t('common.AUDIO'),
            };
         }
         case item?.msgType === 'location':
            return { icon: <LocationIcon className="chat-message-icon" />, label: 'Location' };
         case item?.msgType === 'file':
            return { icon: <DocumentIcon className="chat-message-icon" />, label: 'File' };
         case item?.msgType === 'contact':
            return { icon: <ContactIcon className="chat-message-icon" />, label: 'Contact' };
         case item?.msgType === 'meet': {
            const scheduledTime = formatMeetingDate(item?.msgBody?.meet?.scheduledDateTime);
            return {
               icon: <IconCalendar className="chat-message-icon" />,
               label: scheduledTime ? `${t('calendar.SCHEDULED_ON')} ${scheduledTime}` : t('calendar.MEET'),
            };
         }
         default:
            return { icon: null, label: null };
      }
   }, [item, t, groupNotificationText, formattedText, isMyMessage]);

   return { lastMessageDisplay, isMyMessage };
};
